import { CreateAccountForm } from './types'
import { duplicateErrorCodeList, RegistrationData, RegistrationError, RegistrationStatus, SupportedLocale } from '../../model'
import { AddressFieldSchema, CheckboxFieldSchema, DateOfBirthFieldSchema, FieldType, InputFieldSchema, PhoneFieldSchema, Schema } from '@afterpaytouch/form'
import { TFunction } from 'i18next'
import { emailValidation, namePattern, nameValidation, nameValidationWithLength, requiredAddress, requiredValidation } from './validation'
import { Country } from '@afterpaytouch/portal-api/types'
import { CheckoutAppDispatch, ConsumerState } from '../../state'
import { app } from '../../env'
import { ALLOWED_ADDRESS_TYPES } from '../ShippingAddressForm/utils'
import { InputAddressValidationRule, ProvidedAddress, InputBirthdayDateFormatPosition } from '@afterpaytouch/core'
import { LogEvent, TrackingEvent } from '../../model/monitoring'
import { getAddressOptionalFields } from '../../utils/profile-validation'
import { validatePoBox } from '../ShippingAddressForm/validations'
import { isValidName } from '../../utils/validation'
import { EmailInputError } from './constants'
import { isValidEmail } from '@afterpay/utils'

interface GetSchemaAndFormDataResult {
  schema: Schema<CreateAccountForm>
  initialFormData: CreateAccountForm
}

interface CreateAccountComponents {
  profileCard: () => JSX.Element
  signUpSubText: () => JSX.Element
  duplicateMobileError: () => JSX.Element
  marketingOptIn: () => JSX.Element
  terms: () => JSX.Element
  continueButton: () => JSX.Element
  emailInputError: () => JSX.Element | string
  inlineError: () => JSX.Element
  handleCountryChange: (country: Country) => void
  renderMobileTerms: () => JSX.Element
}

export const getFormData = (
  registrationData: RegistrationData | undefined,
  givenNames: string,
  surname: string,
  defaultMobile: string,
  isMobileLogin: boolean,
  consumerMobile: string | undefined,
  parseMobile: (mobile: string | undefined) => string,
  country: Country
): CreateAccountForm => {
  const addressData = {
    address1: registrationData?.address1 ?? '',
    address2: registrationData?.address2 ?? '',
    area2: registrationData?.area2 ?? '',
    suburb: registrationData?.suburb ?? '',
    state: registrationData?.state ?? '',
    postcode: registrationData?.postcode ?? '',
    countryCode: registrationData?.countryCode ?? '',
  }
  return {
    profileCard: undefined,
    signUpSubText: undefined,
    givenNames: isValidName({ name: registrationData?.givenNames ?? givenNames }) ? registrationData?.givenNames ?? givenNames : '',
    otherGivenNames: registrationData?.otherGivenNames ?? '',
    surname: isValidName({ name: registrationData?.surname ?? surname }) ? registrationData?.surname ?? surname : '',
    mobile: isMobileLogin ? parseMobile(consumerMobile) : defaultMobile,
    mobileTerms: undefined,
    dateOfBirth: registrationData?.dateOfBirth ?? '',
    email: isValidEmail(registrationData?.email ?? '') ? registrationData?.email ?? '' : '',
    address: addressData,
    marketingOptIn: country === Country.GB ? registrationData?.marketingOptIn ?? false : undefined,
    terms: undefined,
    inlineError: undefined,
    continueButton: undefined,
  }
}

const getInputFieldSchema = ({ value, label, error = false, options = {}, show = true, otherProps = {}, inline = false }): InputFieldSchema => ({
  type: FieldType.Input,
  show,
  props: {
    label,
    value,
    error,
    ...otherProps,
  },
  options,
  inline,
})

const getCheckboxFieldSchema = ({ value, error = false, otherProps = {}, options = {}, show = true }): CheckboxFieldSchema => ({
  type: FieldType.Checkbox,
  show,
  props: {
    defaultChecked: value,
    ...otherProps,
  },
  options,
})

const getInputBirthdaySchema = ({ locale, label, error = false, value, options, otherValidations = {}, otherProps = {} }): DateOfBirthFieldSchema => ({
  type: FieldType.DateOfBirth,
  show: true,
  props: {
    label,
    defaultValue: value,
    locale: locale as SupportedLocale,
    hasDateFormatLabel: true,
    error,
    ...otherProps,
  },
  options,
  ...otherValidations,
})

const getPhoneFieldSchema = ({
  value,
  error = false,
  otherProps = {},
  options = {},
  otherValidations = {},
  validateOnLoad = false,
  show = true,
}): PhoneFieldSchema => ({
  type: FieldType.Phone,
  show,
  validateOnLoad,
  props: {
    value,
    error,
    ...otherProps,
  },
  options,
  ...otherValidations,
})

const getAddressFieldSchema = ({ value, error = false, otherProps = {}, options = {}, validateOnLoad = false, disabled, show = true }): AddressFieldSchema => ({
  type: FieldType.Address,
  apiKey: app.GOOGLEMAPS_API_KEY,
  show: true,
  validateOnLoad,
  props: {
    initialAddress: value,
    error,
    disabled,
    ...otherProps,
  },
  options,
})

export const getSchema = (
  registrationData: RegistrationData | undefined,
  components: CreateAccountComponents,
  t: TFunction,
  givenNames: string,
  surname: string,
  enableEditName: boolean,
  consumer: ConsumerState,
  locale: SupportedLocale,
  country: Country,
  dobValidations: Omit<DateOfBirthFieldSchema, 'type'>,
  defaultMobile: string,
  isMobileLogin: boolean,
  isExpress: boolean,
  isAuthRequired: boolean,
  parseMobile: (mobile: string | undefined) => string,
  supportedPrefixes: Country[],
  dispatch: CheckoutAppDispatch,
  isManualAddress: boolean,
  initialAddresses: ProvidedAddress[] | ProvidedAddress,
  logEvent: LogEvent,
  registrationError: RegistrationError | undefined,
  registrationStatus: RegistrationStatus,
  emailInputError: EmailInputError | undefined,
  handleGeocodeResult: (success: boolean) => void,
  isArcadeDesignLanguage: boolean
): GetSchemaAndFormDataResult => {
  const { mobile: consumerMobile } = consumer
  const initialFormData = getFormData(registrationData, givenNames, surname, defaultMobile, isMobileLogin, consumerMobile, parseMobile, country)
  const { givenNames: initalGivenames, otherGivenNames, surname: initialSurname, dateOfBirth, mobile, email, address, marketingOptIn } = initialFormData
  const required = requiredValidation(t)
  const namePatterns = namePattern(t)
  const validEmail = emailValidation(t)
  const dobValidation = dobValidations
  const isDuplicatePhoneNumberError =
    registrationStatus === RegistrationStatus.Error && registrationError?.error !== undefined && duplicateErrorCodeList.includes(registrationError?.error)
  const showMobileTerms = [Country.US, Country.CA, Country.NZ].includes(country)
  const hideProfileCard = isExpress && isMobileLogin
  const allowedCountries = supportedPrefixes.map((country) => {
    return {
      countryCode: country,
      name: t('common:countryName', { context: country }),
    }
  })

  const schema: Schema<CreateAccountForm> = {
    profileCard: {
      type: FieldType.Component,
      Component: components.profileCard,
      show: !hideProfileCard,
    },
    signUpSubText: {
      type: FieldType.Component,
      Component: components.signUpSubText,
    },
    givenNames: getInputFieldSchema({
      label: t('signUp:firstNameLabel'),
      value: initalGivenames,
      show: enableEditName,
      otherProps: {
        capitalize: true,
        'data-dd-action-name': 'Create Account First Name Input',
      },
      options: {
        ...required('givenNames'),
        ...namePatterns('givenNames'),
        ...nameValidationWithLength({ name: 'givenNames', country, t }),
      },
      inline: true,
    }),
    otherGivenNames: getInputFieldSchema({
      label: t('signUp:middleNameLabel'),
      value: otherGivenNames,
      show: enableEditName,
      otherProps: {
        capitalize: true,
        'data-dd-action-name': 'Create Account Middle Name Input',
      },
      options: {
        ...namePatterns('otherGivenNames'),
        ...nameValidation(t),
      },
      inline: true,
    }),
    surname: getInputFieldSchema({
      label: t('signUp:lastNameLabel'),
      value: initialSurname,
      show: enableEditName,
      otherProps: {
        capitalize: true,
        'data-dd-action-name': 'Create Account Last Name Input',
      },
      options: {
        ...required('surname'),
        ...namePatterns('surname'),
        ...nameValidationWithLength({ name: 'surname', country, t }),
      },
    }),
    email: getInputFieldSchema({
      label: t('common:email'),
      value: email,
      error: emailInputError !== undefined,
      show: isMobileLogin || emailInputError !== undefined,
      otherProps: {
        errorText: emailInputError !== undefined ? components.emailInputError() : '',
        'data-dd-action-name': 'Create Account Email Input',
      },
      options: {
        ...required('email'),
        ...validEmail('email'),
      },
    }),
    mobile: getPhoneFieldSchema({
      value: mobile,
      show: isAuthRequired || isMobileLogin,
      otherProps: {
        label: t('signUp:mobileInputLabel'),
        allowedCountries: allowedCountries.length > 0 ? allowedCountries : undefined,
        selectedCountry: country,
        onCountryChange: components.handleCountryChange,
        'data-dd-action-name': 'Create Account Mobile Input',
        ...(isDuplicatePhoneNumberError && {
          error: isDuplicatePhoneNumberError,
          errorText: isDuplicatePhoneNumberError && components.duplicateMobileError(),
        }),
      },
      options: {
        ...required('mobile'),
      },
      otherValidations: {
        invalid: t('signUp:error.invalidMobile'),
      },
    }),
    mobileTerms: {
      type: FieldType.Component,
      show: showMobileTerms,
      Component: components.renderMobileTerms,
    },
    dateOfBirth: getInputBirthdaySchema({
      value: dateOfBirth,
      label: t('signUp:dateOfBirthLabel'),
      locale,
      options: {
        ...required('dateOfBirth'),
      },
      otherProps: {
        dateFormatPosition: InputBirthdayDateFormatPosition.SecondaryLabel,
        hasDateFormatLabel: !isArcadeDesignLanguage,
        'data-dd-action-name': 'Create Account Date Of Birth Input',
      },
      otherValidations: dobValidation,
    }),
    address: getAddressFieldSchema({
      value: address,
      disabled: false,
      otherProps: {
        defaultExpanded: isManualAddress,
        label: t('signUp:address'),
        manualAddressLabel: t('signUp:findAddressLabel'),
        onManualAddressLabelClick: () => logEvent(TrackingEvent.PRESSED_MANUAL_ADDRESS_TOGGLE),
        autoFocus: false,
        address1Label: t('signUp:address1Label'),
        address2Label: t('signUp:address2Label'),
        postcodeLabel: t('signUp:postcodeLabel'),
        componentRestrictions: { country },
        onGeocodeResult: handleGeocodeResult,
        focusOnSelect: false,
        initialAddress: initialAddresses,
        suburbLabel: t('signUp:suburbLabel'),
        stateLabel: t('signUp:stateLabel'),
        searchAddressLabel: t('signUp:searchAddressLabel'),
        optionalManualAddressFields: getAddressOptionalFields(country),
        allowedAddressType: ALLOWED_ADDRESS_TYPES,
        validateRule: InputAddressValidationRule.ByAddress,
        'data-dd-action-name': 'Create Account Address Input',
      },
      options: {
        validate: {
          ...requiredAddress({ name: 'address', country, t }),
          ...validatePoBox(t('signUp:error.poBoxError')),
        },
      },
    }),
    marketingOptIn: getCheckboxFieldSchema({
      value: marketingOptIn,
      show: country === Country.GB,
      otherProps: {
        label: components.marketingOptIn,
        size: 'Small',
        id: 'marketingOptInCheckbox',
        'data-dd-action-name': 'Create Account Marketing Opt In Checkbox',
      },
    }),
    terms: {
      type: FieldType.Component,
      Component: components.terms,
    },
    inlineError: {
      type: FieldType.Component,
      show: registrationStatus === RegistrationStatus.Error && !isDuplicatePhoneNumberError,
      Component: components.inlineError,
    },
    continueButton: {
      type: FieldType.Component,
      Component: components.continueButton,
    },
  }
  return { schema, initialFormData }
}
